import {
  Box,
  Button,
  Disclaimer,
  Div,
  H5,
  TooltipWrapper,
  lightTheme,
} from '@vgrubs/components';
import type { AnnouncementActivity } from '../../../types/api/announcements.types';
import { ConversationAvatar } from '../../Chat/components/ConversationAvatar';
import {
  formatSeenAt,
  formatSeenAtFull,
  pairLabel,
  viewerName,
} from '../helpers/announcementViewers';
import { useAnnouncementActivity } from '../hooks/useAnnouncementActivity';
import {
  ActivityList,
  ActivityRow,
  ActivityText,
} from './AnnouncementActivityFeed.styled';

const ACTION_VERB: Record<AnnouncementActivity['action'], string> = {
  seen: 'viewed',
  dismissed: 'dismissed',
};

/**
 * "Sarah Kim viewed on merchant iOS · 2h ago", newest first. The app is
 * spelled out because the same person can be a merchant on the phone and an
 * admin on the web, and "viewed on Web" would not say which.
 */
export const AnnouncementActivityFeed = ({ id }: { id: number }) => {
  const feed = useAnnouncementActivity(id);

  return (
    <Box $flex $column $gap="12px" $minWidth="0">
      <H5 $m="0">Recent activity</H5>

      {!feed.isLoading && !feed.events.length && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Nothing yet. Activity is only recorded since this feature went live.
        </Disclaimer>
      )}

      <ActivityList>
        {feed.events.map((event) => (
          <ActivityRow key={event.id}>
            <ConversationAvatar name={viewerName(event)} size={24} />
            <ActivityText>
              <b>{viewerName(event)}</b> {ACTION_VERB[event.action]} on{' '}
              {pairLabel(event.app, event.platform)}
            </ActivityText>
            <TooltipWrapper tooltip={formatSeenAtFull(event.createdAt)}>
              <Disclaimer $m="0" $color={lightTheme.lightGray} $noWrap>
                {formatSeenAt(event.createdAt)}
              </Disclaimer>
            </TooltipWrapper>
          </ActivityRow>
        ))}
      </ActivityList>

      {feed.hasNextPage && (
        <Div $flex $justifyCenter>
          <Button
            $small
            $loading={feed.isFetchingNextPage}
            onClick={() => feed.fetchNextPage()}
          >
            Show more
          </Button>
        </Div>
      )}
    </Box>
  );
};
